/**
 * Every server call the dashboard makes, as TanStack Query hooks.
 *
 * Centralised so that cache keys are consistent: a mutation that invalidates
 * `["sites", orgId]` and a query registered under `["site", orgId]` would
 * leave a screen showing stale data with no error anywhere, which is the
 * failure mode this file exists to prevent.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { api } from "./client";
import type {
  AcquisitionReport,
  AdminJob,
  AdminPlan,
  BillingOverview,
  PlanSummary,
  AdminOrganization,
  Analytics,
  AuditEvent,
  ContentStats,
  Conversation,
  ConversationDetail,
  DocumentDetail,
  DocumentSummary,
  ErasureReceipt,
  FaqEntry,
  FaqStatus,
  FaqSummary,
  Job,
  Membership,
  OrganizationUsage,
  PairingCode,
  PlatformStats,
  RetentionSettings,
  Site,
  User,
  UserDeleted,
  WidgetConfig,
} from "./types";

export const keys = {
  me: ["me"] as const,
  organizations: ["organizations"] as const,
  sites: (org: string) => ["sites", org] as const,
  site: (org: string, site: string) => ["site", org, site] as const,
  content: (org: string, site: string) => ["content", org, site] as const,
  documents: (org: string, site: string, filters: unknown) =>
    ["documents", org, site, filters] as const,
  document: (org: string, site: string, id: string) => ["document", org, site, id] as const,
  jobs: (org: string, site: string) => ["jobs", org, site] as const,
  conversations: (org: string, site: string) => ["conversations", org, site] as const,
  conversation: (org: string, site: string, id: string) =>
    ["conversation", org, site, id] as const,
  analytics: (org: string, site: string, days: number) =>
    ["analytics", org, site, days] as const,
  adminStats: ["admin", "stats"] as const,
  adminAcquisition: (days: number) => ["admin", "acquisition", days] as const,
  adminOrganizations: ["admin", "organizations"] as const,
  adminUsers: ["admin", "users"] as const,
  adminJobs: (status?: string) => ["admin", "jobs", status ?? "all"] as const,
  adminUsage: (org: string) => ["admin", "usage", org] as const,
  adminAudit: (org?: string) => ["admin", "audit", org ?? "all"] as const,
  plans: ["plans"] as const,
  billing: (org: string) => ["billing", org] as const,
  adminPlans: ["admin", "plans"] as const,
  retention: (org: string) => ["retention", org] as const,
  faq: (org: string, site: string, status?: string) =>
    ["faq", org, site, status ?? "all"] as const,
  faqSummary: (org: string, site: string) => ["faq-summary", org, site] as const,
};

const org = (id: string) => `/api/v1/orgs/${id}`;
const site = (o: string, s: string) => `${org(o)}/sites/${s}`;

// --- identity ----------------------------------------------------------------

export function useMe(options?: Partial<UseQueryOptions<User>>) {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<User>("/api/v1/users/me"),
    ...options,
  });
}

export function useOrganizations() {
  return useQuery({
    queryKey: keys.organizations,
    queryFn: () => api.get<Membership[]>("/api/v1/orgs"),
  });
}

// --- sites -------------------------------------------------------------------

export function useSites(orgId: string) {
  return useQuery({
    queryKey: keys.sites(orgId),
    queryFn: () => api.get<Site[]>(`${org(orgId)}/sites`),
    enabled: Boolean(orgId),
  });
}

export function useSite(orgId: string, siteId: string, refetchMs?: number) {
  return useQuery({
    queryKey: keys.site(orgId, siteId),
    queryFn: () => api.get<Site>(site(orgId, siteId)),
    enabled: Boolean(orgId && siteId),
    // Pairing completes out of band: the plugin redeems the code against the
    // API, and nothing tells this browser. Polling is how the screen stops
    // saying "not connected" without the customer reloading and wondering
    // whether they did it wrong.
    refetchInterval: refetchMs,
  });
}

export function useCreateSite(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { name: string; url: string }) =>
      api.post<Site>(`${org(orgId)}/sites`, body),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.sites(orgId) }),
  });
}

export function useUpdateSite(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<{ name: string; allowed_origins: string[]; widget_config: WidgetConfig }>) =>
      api.patch<Site>(site(orgId, siteId), body),
    onSuccess: (updated) => {
      // Seed the cache with the response rather than only invalidating: the
      // customiser's live preview reads this, and a refetch round trip on a
      // free-tier API makes every colour change flicker.
      client.setQueryData(keys.site(orgId, siteId), updated);
      void client.invalidateQueries({ queryKey: keys.sites(orgId) });
    },
  });
}

export function useDeleteSite(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete<void>(site(orgId, siteId)),
    onSuccess: () => {
      // Removed, not invalidated: an invalidation refetches a site that no
      // longer exists, and its 404 flashes on screen before the redirect.
      client.removeQueries({ queryKey: keys.site(orgId, siteId) });
      void client.invalidateQueries({ queryKey: keys.sites(orgId) });
    },
  });
}

export function useCreatePairingCode(orgId: string, siteId: string) {
  return useMutation({
    mutationFn: () => api.post<PairingCode>(`${site(orgId, siteId)}/pairing-codes`, {}),
  });
}

// --- content -----------------------------------------------------------------

export function useContentStats(orgId: string, siteId: string, refetchMs?: number) {
  return useQuery({
    queryKey: keys.content(orgId, siteId),
    queryFn: () => api.get<ContentStats>(`${site(orgId, siteId)}/content`),
    enabled: Boolean(orgId && siteId),
    refetchInterval: refetchMs,
  });
}

export interface DocumentFilters {
  search?: string;
  source_type?: string;
  excluded?: boolean;
}

export function useDocuments(orgId: string, siteId: string, filters: DocumentFilters) {
  const params = new URLSearchParams();
  if (filters.search) params.set("search", filters.search);
  if (filters.source_type) params.set("source_type", filters.source_type);
  if (filters.excluded !== undefined) params.set("excluded", String(filters.excluded));

  return useQuery({
    queryKey: keys.documents(orgId, siteId, filters),
    queryFn: () =>
      api.get<DocumentSummary[]>(`${site(orgId, siteId)}/documents?${params.toString()}`),
    enabled: Boolean(orgId && siteId),
  });
}

export function useDocument(orgId: string, siteId: string, documentId: string | null) {
  return useQuery({
    queryKey: keys.document(orgId, siteId, documentId ?? ""),
    queryFn: () => api.get<DocumentDetail>(`${site(orgId, siteId)}/documents/${documentId}`),
    enabled: Boolean(orgId && siteId && documentId),
  });
}

export function useSetExclusion(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ documentId, excluded }: { documentId: string; excluded: boolean }) =>
      api.patch<DocumentSummary>(`${site(orgId, siteId)}/documents/${documentId}`, { excluded }),
    onSuccess: () => {
      // Both: the list shows the new state and the summary shows the new count.
      void client.invalidateQueries({ queryKey: ["documents", orgId, siteId] });
      void client.invalidateQueries({ queryKey: keys.content(orgId, siteId) });
    },
  });
}

// --- syncing -----------------------------------------------------------------

export function useJobs(orgId: string, siteId: string, refetchMs?: number) {
  return useQuery({
    queryKey: keys.jobs(orgId, siteId),
    queryFn: () => api.get<Job[]>(`${site(orgId, siteId)}/jobs`),
    enabled: Boolean(orgId && siteId),
    refetchInterval: refetchMs,
  });
}

export function useStartSync(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (type: string = "full_sync") =>
      api.post<Job>(`${site(orgId, siteId)}/sync`, { type }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.jobs(orgId, siteId) }),
  });
}

export function useCancelJob(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<Job>(`${site(orgId, siteId)}/jobs/${jobId}/cancel`),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.jobs(orgId, siteId) }),
  });
}

// --- conversations and analytics ---------------------------------------------

export function useConversations(orgId: string, siteId: string) {
  return useQuery({
    queryKey: keys.conversations(orgId, siteId),
    queryFn: () => api.get<Conversation[]>(`${site(orgId, siteId)}/conversations`),
    enabled: Boolean(orgId && siteId),
  });
}

export function useConversation(orgId: string, siteId: string, id: string | null) {
  return useQuery({
    queryKey: keys.conversation(orgId, siteId, id ?? ""),
    queryFn: () => api.get<ConversationDetail>(`${site(orgId, siteId)}/conversations/${id}`),
    enabled: Boolean(orgId && siteId && id),
  });
}

export function useAnalytics(orgId: string, siteId: string, days: number) {
  return useQuery({
    queryKey: keys.analytics(orgId, siteId, days),
    queryFn: () => api.get<Analytics>(`${site(orgId, siteId)}/analytics?days=${days}`),
    enabled: Boolean(orgId && siteId),
  });
}

// --- admin -------------------------------------------------------------------

export function usePlatformStats() {
  return useQuery({
    queryKey: keys.adminStats,
    queryFn: () => api.get<PlatformStats>("/api/v1/admin/stats"),
  });
}

export function useAcquisition(days: number) {
  return useQuery({
    queryKey: keys.adminAcquisition(days),
    queryFn: () => api.get<AcquisitionReport>(`/api/v1/admin/acquisition?days=${days}`),
  });
}

export function useAdminOrganizations() {
  return useQuery({
    queryKey: keys.adminOrganizations,
    queryFn: () => api.get<AdminOrganization[]>("/api/v1/admin/organizations"),
  });
}

export function useAdminUsers() {
  return useQuery({
    queryKey: keys.adminUsers,
    queryFn: () => api.get<User[]>("/api/v1/admin/users"),
  });
}

export function useAdminJobs(status?: string, refetchMs?: number) {
  return useQuery({
    queryKey: keys.adminJobs(status),
    queryFn: () =>
      api.get<AdminJob[]>(`/api/v1/admin/jobs${status ? `?status=${status}` : ""}`),
    refetchInterval: refetchMs,
  });
}

export function useRequeueJob() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (jobId: string) => api.post<AdminJob>(`/api/v1/admin/jobs/${jobId}/requeue`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ["admin", "jobs"] });
      // A requeue is an audited action, so the audit view is now stale too.
      void client.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

export function useOrganizationUsage(orgId: string) {
  return useQuery({
    queryKey: keys.adminUsage(orgId),
    queryFn: () => api.get<OrganizationUsage>(`/api/v1/admin/organizations/${orgId}/usage`),
    enabled: Boolean(orgId),
  });
}

export function useSetOrganizationStatus() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, suspend }: { orgId: string; suspend: boolean }) =>
      api.post<AdminOrganization>(
        `/api/v1/admin/organizations/${orgId}/${suspend ? "suspend" : "reinstate"}`
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.adminOrganizations });
      void client.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

export function useAuditLog(orgId?: string) {
  return useQuery({
    queryKey: keys.adminAudit(orgId),
    queryFn: () =>
      api.get<AuditEvent[]>(
        `/api/v1/admin/audit${orgId ? `?organization_id=${orgId}` : ""}`
      ),
  });
}

export function useImpersonate() {
  return useMutation({
    mutationFn: ({ userId, reason }: { userId: string; reason: string }) =>
      api.post<{ access_token: string; email: string; user_id: string }>(
        `/api/v1/admin/users/${userId}/impersonate`,
        { reason }
      ),
  });
}

export function useDeleteUser() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<UserDeleted>(`/api/v1/admin/users/${userId}?confirm=true`),
    onSuccess: () => {
      // Organizations and totals change too: the tenants the account alone
      // owned went with it.
      void client.invalidateQueries({ queryKey: ["admin"] });
    },
  });
}

// --- billing -----------------------------------------------------------------

export function usePlans() {
  return useQuery({
    queryKey: keys.plans,
    queryFn: () => api.get<PlanSummary[]>("/api/v1/plans"),
  });
}

export function useBilling(orgId: string) {
  return useQuery({
    queryKey: keys.billing(orgId),
    queryFn: () => api.get<BillingOverview>(`${org(orgId)}/billing`),
    enabled: Boolean(orgId),
  });
}

export function useStartCheckout(orgId: string) {
  return useMutation({
    mutationFn: (planCode: string) =>
      api.post<{ url: string }>(`${org(orgId)}/billing/checkout`, { plan_code: planCode }),
    // Not a router navigation: the destination is Stripe's domain, so this
    // has to be a full page load.
    onSuccess: (result) => {
      window.location.assign(result.url);
    },
  });
}

export function useOpenPortal(orgId: string) {
  return useMutation({
    mutationFn: () => api.post<{ url: string }>(`${org(orgId)}/billing/portal`),
    onSuccess: (result) => {
      window.location.assign(result.url);
    },
  });
}

export function useAdminPlans() {
  return useQuery({
    queryKey: keys.adminPlans,
    queryFn: () => api.get<AdminPlan[]>("/api/v1/admin/plans"),
  });
}

export function useUpdatePlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ planId, patch }: { planId: string; patch: Partial<AdminPlan> }) =>
      api.patch<AdminPlan>(`/api/v1/admin/plans/${planId}`, patch),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.adminPlans });
      // The public list carries `self_service`, which flips the moment a
      // Stripe price id is set.
      void client.invalidateQueries({ queryKey: keys.plans });
    },
  });
}

export function useAssignPlan() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, planCode }: { orgId: string; planCode: string }) =>
      api.post(`/api/v1/admin/organizations/${orgId}/plan`, { plan_code: planCode }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.adminOrganizations });
      void client.invalidateQueries({ queryKey: ["admin", "audit"] });
    },
  });
}

// --- data protection ---------------------------------------------------------

export function useRetention(orgId: string) {
  return useQuery({
    queryKey: keys.retention(orgId),
    queryFn: () => api.get<RetentionSettings>(`${org(orgId)}/privacy/retention`),
    enabled: Boolean(orgId),
  });
}

export function useSetRetention(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (days: number | null) =>
      api.put<RetentionSettings>(`${org(orgId)}/privacy/retention`, {
        conversation_retention_days: days,
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.retention(orgId) });
    },
  });
}

export function useEraseVisitor(orgId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (visitorId: string) =>
      api.delete<ErasureReceipt>(
        `${org(orgId)}/privacy/visitors/${encodeURIComponent(visitorId)}`,
      ),
    onSuccess: () => {
      // Transcripts and every count derived from them are now wrong. Which
      // site the visitor talked to is not known here, so the whole family goes.
      void client.invalidateQueries({ queryKey: ["conversations"] });
      void client.invalidateQueries({ queryKey: ["analytics"] });
    },
  });
}

/**
 * Download the tenant's data as a file.
 *
 * The blob URL is revoked immediately after the click. Left alive it pins the
 * entire export in memory for the lifetime of the document, which for a large
 * store is hundreds of megabytes held for no reason.
 */
export function useExportData(orgId: string) {
  return useMutation({
    mutationFn: async () => {
      const file = await api.download(`${org(orgId)}/privacy/export`);
      const url = URL.createObjectURL(file.blob);

      try {
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = file.filename;
        anchor.click();
      } finally {
        URL.revokeObjectURL(url);
      }

      return file.filename;
    },
  });
}

// --- Q&A ---------------------------------------------------------------------
//
// Every mutation invalidates both the list and the summary: the summary feeds
// the "N waiting" badge in the site nav, and a badge that still says 3 after
// you answered one is the kind of thing that makes a dashboard feel broken.

export function useFaqEntries(orgId: string, siteId: string, status?: FaqStatus) {
  return useQuery({
    queryKey: keys.faq(orgId, siteId, status),
    queryFn: () =>
      api.get<FaqEntry[]>(
        `${site(orgId, siteId)}/faq${status ? `?status=${status}` : ""}`,
      ),
    enabled: Boolean(orgId && siteId),
  });
}

export function useFaqSummary(orgId: string, siteId: string) {
  return useQuery({
    queryKey: keys.faqSummary(orgId, siteId),
    queryFn: () => api.get<FaqSummary>(`${site(orgId, siteId)}/faq/summary`),
    enabled: Boolean(orgId && siteId),
  });
}

function invalidateFaq(client: ReturnType<typeof useQueryClient>, orgId: string, siteId: string) {
  void client.invalidateQueries({ queryKey: ["faq", orgId, siteId] });
  void client.invalidateQueries({ queryKey: keys.faqSummary(orgId, siteId) });
}

export function useCreateFaqEntry(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: { question: string; answer?: string | null }) =>
      api.post<FaqEntry>(`${site(orgId, siteId)}/faq`, body),
    onSuccess: () => invalidateFaq(client, orgId, siteId),
  });
}

export function useUpdateFaqEntry(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      entryId,
      patch,
    }: {
      entryId: string;
      patch: { question?: string; answer?: string; status?: FaqStatus };
    }) => api.patch<FaqEntry>(`${site(orgId, siteId)}/faq/${entryId}`, patch),
    onSuccess: () => invalidateFaq(client, orgId, siteId),
  });
}

export function useDeleteFaqEntry(orgId: string, siteId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) => api.delete<void>(`${site(orgId, siteId)}/faq/${entryId}`),
    onSuccess: () => invalidateFaq(client, orgId, siteId),
  });
}
